import { useEffect, useState } from 'react';

type Trigger = undefined | ((isVisible: boolean) => void);

type Status = {
  visibility: boolean;
  subscribe: (cb: (isVisible: boolean) => void) => void;
};

export function useKeyboardVisibility<K extends keyof Status>(
  key: K
): Status[K];
export function useKeyboardVisibility(): Status;
export function useKeyboardVisibility(key?: keyof Status) {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  let trigger: Trigger = undefined;

  useEffect(() => {
    if (trigger) trigger(isVisible);
  }, [trigger, isVisible]);

  function onFocusIn({ target }: FocusEvent) {
    if (target instanceof HTMLInputElement) {
      if (target.tagName === 'INPUT') {
        if (target.type === 'text' || target.type === 'number') {
          setIsVisible(true);
        }
      }
    }
  }

  function onFocusOut({ target }: FocusEvent) {
    if (target instanceof HTMLInputElement) {
      if (target.tagName === 'INPUT') {
        if (target.type === 'text' || target.type === 'number') {
          setIsVisible(false);
        }
      }
    }
  }

  useEffect(() => {
    window.addEventListener('focusin', onFocusIn);
    window.addEventListener('focusout', onFocusOut);

    return () => {
      window.removeEventListener('focusin', onFocusIn);
      window.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  const data: Status = {
    visibility: isVisible,
    subscribe(cb: (value: boolean) => void) {
      trigger = cb;
    },
  };

  if (key) return data[key];

  return data;
}
